import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { RealtimePostgresUpdatePayload } from '@supabase/supabase-js';

interface BookingRecord {
  id: string;
  status: string;
}

interface BookingStatusContextType {
  lastStatusChange: {
    bookingId: string;
    oldStatus: string;
    newStatus: string;
    timestamp: string;
  } | null;
}

// Create context with a default value
const BookingStatusContext = createContext<BookingStatusContextType>({
  lastStatusChange: null
});

// Hook implementation as a named function
function useBookingStatus() {
  const context = useContext(BookingStatusContext);
  if (!context) {
    throw new Error('useBookingStatus must be used within BookingStatusProvider');
  }
  return context;
}

// Provider as a named function component
function BookingStatusProvider({ children }: { children: React.ReactNode }) {
  const [lastStatusChange, setLastStatusChange] = useState<BookingStatusContextType['lastStatusChange']>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🔄 BookingStatusProvider: Initializing...');
    
    // Validate Supabase client
    if (!supabase) {
      console.error('❌ BookingStatusProvider: Supabase client is not initialized');
      setSubscriptionError('Supabase client not initialized');
      return;
    }

    try {
      console.log('📡 BookingStatusProvider: Creating channel...');
      
      // Create the channel with error handling
      const channel = supabase
        .channel('booking-status-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bookings',
            filter: 'status IS NOT NULL'
          },
          (payload) => {
            try {
              console.log('📥 BookingStatusProvider: Raw payload received:', payload);

              const updatePayload = payload as RealtimePostgresUpdatePayload<BookingRecord>;
              const oldRecord = updatePayload.old;
              const newRecord = updatePayload.new;

              console.log('📄 BookingStatusProvider: Parsed records:', { 
                old: oldRecord, 
                new: newRecord 
              });

              // Validate payload data
              if (!oldRecord || !newRecord) {
                console.warn('⚠️ BookingStatusProvider: Missing record data in payload');
                return;
              }

              if (!oldRecord.status || !newRecord.id || !newRecord.status) {
                console.warn('⚠️ BookingStatusProvider: Incomplete record data:', {
                  oldStatus: oldRecord.status,
                  newId: newRecord.id,
                  newStatus: newRecord.status
                });
                return;
              }

              // Only process if status actually changed
              if (oldRecord.status !== newRecord.status) {
                const timestamp = new Date().toISOString();
                
                const change = {
                  bookingId: newRecord.id,
                  oldStatus: oldRecord.status,
                  newStatus: newRecord.status,
                  timestamp
                };

                console.log('✨ BookingStatusProvider: Valid status change detected:', change);
                setLastStatusChange(change);

                // Enhanced logging
                console.group('📋 Booking Status Change Details');
                console.log('Booking ID:', newRecord.id);
                console.log('Old Status:', oldRecord.status);
                console.log('New Status:', newRecord.status);
                console.log('Timestamp:', new Date(timestamp).toLocaleString());
                console.groupEnd();
              } else {
                console.log('ℹ️ BookingStatusProvider: Status unchanged', {
                  id: newRecord.id,
                  status: newRecord.status
                });
              }
            } catch (error) {
              console.error('❌ BookingStatusProvider: Error processing payload:', error);
              setSubscriptionError('Error processing status change');
            }
          }
        )
        .subscribe((status) => {
          console.log('📡 BookingStatusProvider: Subscription status:', status);
          
          if (status === 'SUBSCRIBED') {
            console.log('✅ BookingStatusProvider: Successfully subscribed to booking changes');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ BookingStatusProvider: Channel error');
            setSubscriptionError('Channel error');
          } else if (status === 'TIMED_OUT') {
            console.error('❌ BookingStatusProvider: Subscription timed out');
            setSubscriptionError('Subscription timed out');
          }
        });

      // Cleanup function
      return () => {
        console.log('🧹 BookingStatusProvider: Cleaning up subscription...');
        channel.unsubscribe().then(() => {
          console.log('✅ BookingStatusProvider: Successfully unsubscribed');
        }).catch((error) => {
          console.error('❌ BookingStatusProvider: Error unsubscribing:', error);
        });
      };
    } catch (error) {
      console.error('❌ BookingStatusProvider: Error setting up subscription:', error);
      setSubscriptionError('Error setting up subscription');
    }
  }, []);

  // Log subscription errors
  useEffect(() => {
    if (subscriptionError) {
      console.error('❌ BookingStatusProvider: Subscription error:', subscriptionError);
    }
  }, [subscriptionError]);

  return (
    <BookingStatusContext.Provider value={{ lastStatusChange }}>
      {children}
      {subscriptionError && process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="font-bold">Subscription Error</p>
          <p>{subscriptionError}</p>
        </div>
      )}
    </BookingStatusContext.Provider>
  );
}

// Export everything as a single default object
export default {
  Provider: BookingStatusProvider,
  useBookingStatus
}; 